import '../../../assets/css/website/guides.css';

export default function GettingStarted() {
  return (
    <div className="guide">
      <h1 className="guide__h1">Getting Started Guide</h1>
      <div className="guide__intro">
        <h2 className="guide__h2">What you will learn</h2>
          <p className="guide__description">How to set up your Amplify Loyalty program
            <ol>
              <li>Ways to earn and rewards</li>
              <li>VIP Program</li>
              <li>Promotions</li>
              <li>Giveaways</li>
              <li>Using the reporting dashboard</li>
              <li>Viewing customer interactions and manual point adjustments</li>
            </ol>
          </p>
      </div>
      <iframe className="guide__video" width="560" height="315" src="https://www.youtube.com/embed/kr8Z-XDgKCE?si=lWh8CHOTnyZn50zU" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>

      <h3 className="guide__h3">To get started</h3>
      <p className="guide__description">Make sure that you have created your Amplify Loyalty account and linked your Shopify
        store to your account.
      </p>

      <h2 className="guide__h2">Creating your program</h2>
      <p className="guide__description">After logging into your account on <a href="/login">www.amplifyloyalty.com/login</a>, 
        click on <strong>Program</strong> in the left menu. By default, <strong>Make a Purchase </strong>
        and <strong>Create an Account</strong> will show as being available. To edit them,
        click on the pencil icon and adjust the amount of points earned for each method.
        Once you are satisfied with your selection, select the toggle in the upper right 
        corner to turn it on and click <strong>Save</strong>.
      </p>
      <div className="guide__mini-section">
        <h3 className="guide__h3">Make a Purchase</h3>
        <p className="guide__description">Apply the amount of points a customer
          will earn per $1 spent on every purchase. This must be a whole number such as 
          <strong> 1</strong> and cannot be a partial number like <strong>2.5</strong>.
        </p>
        <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Make+a+purchase.png" alt="Make a purchase" />
      </div>
      <div className="guide__mini-section">
        <h3 className="guide__h3">Create an Account</h3>
        <p className="guide__description">The amount of points a customer will earn 
          when they create their account for the first time. This will not apply to a first 
          purchase and will only apply to when a customer creates their account through your 
          Shopify store.
        </p>
        <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Create+an+account.png" alt="Create an account" />
      </div>
      <div className="guide__mini-section">
        <h3 className="guide__h3">Birthday Reward</h3>
        <p className="guide__description">The amount of points a customer will be rewarded 
          on their birthday. These rewards are sent automatically once the customer has entered
          their birthday through the rewards page on your store. Making <strong>Reward restricted </strong>
          a number higher than <strong>0</strong> will only give the customer their reward if their birthday
          comes after the specified number of days from when they input it. For example, if this is set to
          30 days and the customer enters their birthday on January 1st, then their birthday must be on 
          January 31st or later to receive their reward this year. This is meant to prevent gaming the system
          to get a reward right away.
        </p>
        <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Birthday+reward.png" alt="Birthday reward" />
      </div>
      <div className="guide__mini-section">
        <h3 className="guide__h3">Like Facebook Page</h3>
        <p className="guide__description">Reward customers with points for liking your page on Facebook. 
          Make sure to link the entire link and not just the username/handle.
        </p>
        <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Like+Facebook+page.png" alt="Like Facebook page" />
      </div>
      <div className="guide__mini-section">
        <h3 className="guide__h3">Follow Instagram</h3>
        <p className="guide__description">Reward customers with points for folowing your page on Instagram. 
          Make sure to link the entire link and not just the username/handle.
        </p>
        <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Follow+Instagram.png" alt="Follow Instagram" />
      </div>

      <h2 className="guide__h2">Setting up Rewards</h2>
      <section className="guide__mini-section">
        <p className="guide__description">Now that your customers have ways to earn points, it's time to
          set up ways for them to spend them. Click on <strong>Create reward</strong> to create your first
          reward.
        </p>
        <div className="guide__note">
          <div className="guide__note__tooltip">
            <div className="tooltip__icon guide__note__tooltip-icon">?</div>
            <div className="guide__note__tooltip-description">Note</div>
          </div>
          <p className="guide__note__description">What you name these rewards are what the
            customer will see when they go to redeem it. Make sure that it accurately describes
            what the reward is and try to keep the name as minimal as possible. For example,
            instead of <strong>Get $10 off of the T-Shirts Collection</strong> try something like
            <strong> $10 Off Tees</strong>.
          </p>
        </div>
        <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Create+reward.png" alt="Create reward" />
      </section>
      <section className="guide__mini-section">
        <h3 className="guide__h3">Type of Reward</h3>
        <p className="guide__description">You have the option to choose between 
          <strong> Amount off</strong>, <strong>Percent off</strong>, and <strong>Free Shipping</strong>.
          If you want to make sure the customer gets a certain percentage back on the amount of 
          money they spend, then it is recommended that you use primarily <strong>Amount off </strong>
          coupons as rewards. That way you can see what percentage they earn back based on 
          how many points the reward costs and the value that it has.
        </p>
      </section>
      <section className="guide__mini-section">
        <h3 className="guide__h3">Usage limit</h3>
        <p className="guide__description">This is always set to 1 by default and it
          is recommended you keep it to 1 use per redemption. Otherwise, the customer will be
          able to redeem it up to the number of times that you specify.
        </p>
      </section>
      <section className="guide__mini-section">
        <h3 className="guide__h3">Set expiration</h3>
        <p className="guide__description">Turned off by default, you are able to set an
          expiration for the reward after they redeem it. If it is turned on and set to 
          30 days for example, then <strong>30 days after the customer has redeemed the 
          reward</strong> it will expire and the points will not be returned to the customer. 
          This is used to incentivize them to use it after redeeming.
        </p>
      </section>
      <section className="guide__mini-section">
        <h3 className="guide__h3">Minimum order value</h3>
        <p className="guide__description">If you want the customer to meet a certain minimum
          order amount to qualify to use the reward then you can set it here. If it is set to 
          $30 for example, then the customer's cart subtotal (before shipping and taxes) 
          must equal $30 or more before they can use this reward.
        </p>
      </section>

      <h2 className="guide__h2">VIP Program</h2>
      <p className="guide__description">VIP Tiers are ways for customers to earn extra
        rewards based on how much money they have spent on with your store. You are able 
        to reward them with bonus coupons or increase the amount of points they earn per 
        $1 spent. You are able to <strong>create up to 5 tiers</strong> for your program.
        Click on the <strong>Add tier</strong> button to create your first VIP tier.
      </p>
      <section className="guide__mini-section">
        <div className="guide__note">
          <div className="guide__note__tooltip">
            <div className="tooltip__icon guide__note__tooltip-icon">?</div>
            <div className="guide__note__tooltip-description">Note</div>
          </div>
          <p className="guide__note__description">What you name these VIP Tiers are what
            the customer will see on your rewards page and in their account. Additionally,
            if it is in your plan for customers to stay earning within a certain percentage
            back on their purchases (i.e. 5% back) then make sure that the bonus multiplier
            you set has that in mind (% back shown when creating rewards for your program
            does not take their VIP Tier into account).
          </p>
        </div>
        <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Create+VIP+tier.png" alt="Create VIP tier" />
        <h3 className="guide__h3">Qualifier</h3>
        <p className="guide__description">The dollar amount you set here will determine
          how much a customer needs to spend to enter this VIP tier. It will not take into 
          account purchases that were made before the VIP program was set to active.
        </p>
      </section>
      <section className="guide__mini-section">
        <h3 className="guide__h3">Purchase point multiplier</h3>
        <p className="guide__description">Inputting a number higher than <strong>1 </strong>
          will increase the amount of points earned per $1 spent multiplicatively. For example,
          if your program is set to reward 1 point for every $1 spent and you input <strong>1.1</strong>,
          the customer will now earn 1.1 point (rounded to the nearest point on the subtotal) 
          for every $1 they spend when they are qualified for this VIP tier. Subsequent VIP tiers
          must have an equal or larger <strong>Purchase point multiplier</strong> than the 
          previous tier.
        </p>
      </section>
      <section className="guide__mini-section">
        <h3 className="guide__h3">Bonus Rewards</h3>
        <p className="guide__description">In addition to points, you can award coupons for your
          store. To create a new Bonus Reward, click on the 
          <span className="vip-form__add-bonus"> + Reward</span> button.
        </p>
        <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Bonus+reward.png" alt="Bonus reward" />
        <p className="guide__description">Bonus rewards are perfect for getting creative with
          VIP tier rewards. Instead of (or in addition to) indefinitely earning additional 
          points with a <strong>Purchase point multiplier</strong>, you can give one-time rewards
          for spending a certain amount. This can be a 1 time use % or $ off coupon or it could be 
          something like Free Shipping for life, or even a free product ($25 off a $25 tee 
          with a 1 time use). This is your chance to get creative and really bring your
          VIP Program to life!
        </p>
      </section>

      <h2 className="guide__h2">Promotions</h2>
      <p className="guide__description">
        Promotions are a way to run time-limited increases in the amount of points earned
        <strong> per $1 spent</strong>. Set a period of time you want your promotion to run,
        how much to increase the point multiplier, and what your promotion applies to. Running
        % off sales can get old. Spice it up with an increase in points earned and get your
        customers to come back to redeem their points again later.
      </p>
      <section className="guide__mini-section">
        <img className="guide__img guide__img--lrg guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Promotions+info.png" alt="Promotions info" />
        <div className="guide__note">
          <div className="guide__note__tooltip">
            <div className="tooltip__icon guide__note__tooltip-icon">?</div>
            <div className="guide__note__tooltip-description">Note</div>
          </div>
          <p className="guide__note__description">
            Promotions <strong>do stack point multipliers with VIP tiers</strong>. Meaning that
            if your customer is in a VIP tier earning a 1.25 multiplier and your Promotion increases
            purchase points earned by 1.5, the customer in that VIP tier will earn 1.75x the amount
            of points set in your base program.
          </p>
        </div>
      </section>
      <section className="guide__mini-section">
        <h3 className="guide__h3">Multiplier</h3>
        <p className="guide__description">
          During the Promotion, customers will earn more points on every <strong>purchase</strong> only.
          This <strong>Multiplier</strong> will apply to purchases only and not to other 
          ways to earn like Liking your Facebook page or Creating an Account.
        </p>
      </section>
      <section className="guide__mini-section">
        <h3 className="guide__h3">Applies to</h3>
        <p className="guide__description">
          You can specify certain collections or products that you want to apply extra points
          on Promotions to. This can be useful to create timely promotions on products
          or collections to add variety to your email strategy and keep engaging customers.
        </p>
      </section>
      <section className="guide__mini-section">
        <h3 className="guide__h3">Scheduling</h3>
        <p className="guide__description">
          Select a <strong>Start time</strong> and <strong>End time</strong> for your Promotion
          to run. Set it and forget it and let us take care of the rest. Your customers will 
          start earning extra points when your Promotion begins and it will automatically return
          to your base program amount at the conclusion.
        </p>
      </section>
      <section className="guide__mini-section">
        <h3 className="guide__h3">Amplifiers</h3>
        <p className="guide__description">
          Replace Amplifiers that are currently set up on your Shopify store within the
          Shopify theme editor during the scheduled time of your Promotion. This can be very 
          useful so that you don't have to manually update these during the run time. Especially
          if you want to use some sort of promotion language like "for Customer Appreciation 
          Weekend" or "this week only". Similarly, you can update colors to better 
          correspond to a certain holiday.
        </p>
        <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Amplifier+settings.png" alt="Amplifier settings" />
        <div className="guide__note">
          <div className="guide__note__tooltip">
            <div className="tooltip__icon guide__note__tooltip-icon">?</div>
            <div className="guide__note__tooltip-description">Note</div>
          </div>
          <p className="guide__note__description">
            For Amplifier settings to take effect on your store, you must set up the app 
            blocks within your Shopify theme first. They will not be added automatically
            by simply turning on the Amplifier within a Promotion.
          </p>
        </div>
        <p className="guide__description">If you are using the <strong>%points%</strong> tag for
          any Amplifiers then they will be updated to the corresponding increase without you 
          having to turn on an Amplifier for a Promotion. For example, if using the Shopify app
          block for product pages to show how many points a customer will earn and you just want
          the points to update on that page, you do not need to turn on the <strong>Products </strong>
          Amplifier in your Promotion.
        </p>
        <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Amplifier+preview.png" alt="Amplifier preview" />
        <p className="guide__description">
          The preview section is meant to give you an <i>idea</i> of what it will look
          like on your store, but you should always check your store for the final outcome.
        </p>
      </section>
      <section className="guide__mini-section">
        <h3 className="guide__h3">Review</h3>
        <p className="guide__description">
          Take a final look at your Promotion before scheduling. Once you ensure that the 
          settings look correct, click on the <strong>Schedule</strong> button.
        </p>
        <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Promotion+review.png" alt="Promotion review" />
      </section>

      <h2 className="guide__h2">Giveaways</h2>
      <p className="guide__description">
        Here you can create and schedule a giveaway to your customers to reward your best customers, 
        re-engage old ones or incentivize new ones to purchase for the first time. Select
        your audience parameters, create coupons or a points distribution and schedule it out.
      </p>
      <section className="guide__mini-section">
        <h3 className="guide__h3">Segmenting</h3>
        <p className="guide__description">
          Based on how much a customer has spent, how many times they have ordered, and/or if they have
          created an account, you can filter down the segment size for your Giveaway. The audience defined within this section is just the amount of accounts who
          <i> qualify</i> to receive a giveaway, you don't have to send something to everyone
          defined here. As you choose and/or filters to define your audience, you will see the
          size of the segment that qualifies to receive your reward.
        </p>
        <img className="guide__img guide__img--rnd guide__img--lrg" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Giveaways+filtering.png" alt="Giveaways Segmenting" />
        <h3 className="guide__h3">Rewards</h3>
        <p className="guide__description">
          The easiest way to set up a Giveaway is to reward your segment with points. 
          Just assign how many points you want to give away and to how many recipients.
          To add another reward to give away, just click the 
          <span className="update-giveaway-points__add-points"> + More points</span> button. 
          You are able to add as many as you would like so long as the number of recipients
          does not exceed your segment size.
        </p>
        <img className="guide__img guide__img--rnd guide__img--lrg" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Giveaways+Rewards.png" alt="Giveaways Segmenting" />
        <div className="guide__note">
          <div className="guide__note__tooltip">
            <div className="tooltip__icon guide__note__tooltip-icon">?</div>
            <div className="guide__note__tooltip-description">Note</div>
          </div>
          <p className="guide__note__description">
            Giveaway recipients are chosen <strong>at random</strong> unless you give a reward
            to your entire segment size. For example, if your segment size is 5,000 but you only
            have 1,000 points or coupons you want to give away then 1,000 recipients will be chosen
            at random from your 5,000 segment size.
          </p>
        </div>
        <p className="guide__description">
          Similarly to creating coupons in other areas of the Amplify Loyalty application,
          you can create coupons to give to your segment instead of points. It is recommended for
          these that they have an expiration date to further incentivize the customers who
          receive them to use them quickly.
        </p>
        <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Giveaways+Coupons.png" alt="Giveaways Coupons" />
        <h3 className="guide__h3">Review & Schedule</h3>
        <p className="guide__description">
          Once you are satisfied with your Giveaway, you can review it on the final page
          and schedule it out for a later time or launch it right away.
        </p>
        <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Giveaway+Review.png" alt="Giveaways Review" />
        </section>

        <h2 className="guide__h2">Dashboard</h2>
        <p className="guide__description">
          Once you have interactions with your program, and customers are earning and 
          redeeming points, you can use the Dashboard to get an overview of how your
          program is performing. Use this as baseline to see how your customers are 
          interacting with your program during a certain time period and create new ideas on
          how you can improve it.
        </p>
        <section className="guide__mini-section">
          <img className="guide__img guide__img--rnd guide__img--lrg" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Dashboard.png" alt="Dashboard" />
        </section>
        
        <h2 className="guide__h2">Customers</h2>
        <p className="guide__description">
          As you acquire more customers into your program through purchases or created
          accounts, you will be able to view all of them here and see how they have
          interacted with your store as well as make any adustments. If you want to search
          for a specific customer, you can do so by entering their email into the search bar.
          To view the customer's profile, just click on their email within the table.
        </p>
        <section className="guide__mini-section">
          <img className="guide__img guide__img--rnd guide__img--lrg" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Customers+page.png" alt="Customers page" />
        </section>
        <section className="guide__mini-section">
          <h3 className="guide__h3">Customer profile</h3>
          <p className="guide__description">
            Within the customer's profile page, you can see things like their <strong>Order History </strong>
            and different ways they have engaged with your program. 
          </p>
          <img className="guide__img guide__img--rnd guide__img--lrg" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Customer+profile.png" alt="Customers profile" />
          <p className="guide__description">
            If you need to make an
            adjustment to the customer's points, just click on <strong>Make Adjustment</strong>.
            To take points away, just make the number a negative number such as <strong>-50</strong>.
            Know that the <strong>Reason</strong> that you add is customer facing and they will
            see it in their profile.
          </p>
          <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Point+adjustment.png" alt="Customer point adjustment" />
        </section>
        <h2 className="guide__h2">Shopify Integration</h2>
        <p className="guide__description">
          Once the settings of your program are set up, you are ready to create the customer 
          facing side on your Shopify store. Learn how to set up your dedicated loyalty page
          and optional <strong>Amplifiers</strong> in our <a href="/guides/shopify-onboarding">Shopify Onboarding Guide</a>.
        </p>
        <h2 className="guide__h2">Conclusion</h2>
        <p className="guide__description">
          You should now know how to use the Amplify Loyalty application and are ready to
          create your loyalty program! If you come across any issues or need any assistance, 
          please email us at <a href="mailto:trevor@amplifyloyalty.com">trevor@amplifyloyalty.com</a>.
        </p>
    </div>
  )
}